import { LocalServiceEntity, TSelectedSkuItem } from "@elixir/types";
import {
  getServiceDropdownId,
  getServiceIdAndPriceFromServiceDropdownId,
  useDebounce,
} from "@elixir/utils";
import React from "react";
import { ServiceRowItemUi } from "ui";
import usePostCreateService from "../../apis/axon/post-create-service";
import useGetServicesMasterAutosuggest from "../../apis/axon/services/use-get-services-master-autosuggest";

export default function ServiceTableRow({
  service,
  allowRemoveService = false,
  onRemoveService,
  index,
  onQtyChange,
  onAmountChange,
  onDiscountChange,
  onServiceSelect,
  disabled,
  amountError,
  discountError,
  quantityError,
}: {
  allowRemoveService?: boolean;
  service: LocalServiceEntity;
  onRemoveService: () => void;
  onQtyChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAmountChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    flag?: boolean
  ) => void;
  index: number;
  onDiscountChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    flag?: boolean
  ) => void;
  onServiceSelect: ({ service }: { service: TSelectedSkuItem }) => void;
  disabled?: boolean;
  amountError?: boolean;
  discountError?: boolean;
  quantityError?: boolean;
}) {
  const currService: TSelectedSkuItem = React.useMemo(() => {
    return {
      price: service.cost.price,
      service_id: service.id,
      service_name: service.name,
    };
  }, [service]);

  const [serviceInputValue, setServiceInputValue] = React.useState<string>(
    () => currService?.service_name || ""
  );

  const debouncedInputValue = useDebounce(serviceInputValue, 300);

  // This is the elastic service conf autosuggest
  const {
    data: ownerServicesAutosuggestData,
    isLoading: isServiceAutosuggestLoading,
  } = useGetServicesMasterAutosuggest({
    query: debouncedInputValue,
    pageSize: 100,
  });

  const { mutateAsync: createServiceType } = usePostCreateService();

  const [servicesSuggestionList, setservicesSuggestionList] = React.useState<
    TSelectedSkuItem[]
  >(() => []);

  React.useEffect(() => {
    setServiceInputValue(currService?.service_name || "");
  }, [currService]);

  React.useEffect(() => {
    const setServices = async () => {
      setservicesSuggestionList(ownerServicesAutosuggestData?.data || []);
    };

    if (!isServiceAutosuggestLoading) {
      setServices();
    }
  }, [isServiceAutosuggestLoading, ownerServicesAutosuggestData?.data]);

  const serviceSearchOptions = React.useMemo(() => {
    return servicesSuggestionList.map((service) => ({
      name: service.service_name,
      id: getServiceDropdownId(service),
    }));
  }, [servicesSuggestionList]);

  const handleServiceValueChange = (newValue: string) => {
    setServiceInputValue(newValue);

    if (newValue !== currService?.service_name) {
      onServiceSelect({
        service: {
          service_id: null,
          service_name: newValue,
          price: currService.price,
        },
      });
    }
  };

  const handleSuggestionSelect = (
    id: string | number,
    servicesList?: TSelectedSkuItem[]
  ) => {
    const { service_id, price } = getServiceIdAndPriceFromServiceDropdownId(
      `${id}`
    );
    const serviceSelected = (servicesList || servicesSuggestionList).find(
      (service) => service.service_id === service_id && service.price === price
    );

    if (serviceSelected) {
      onServiceSelect({ service: serviceSelected });
    }
  };

  const handleNewCreateService = async ({
    serviceName,
  }: {
    serviceName: string;
  }) => {
    const { data: newService } = await createServiceType({ name: serviceName });

    if (newService) {
      const newServiceData: TSelectedSkuItem = {
        price: 0,
        service_id: Number(newService.id),
        service_name: newService.name,
      };

      const serviceSuggestionList = [...servicesSuggestionList, newServiceData];

      setservicesSuggestionList(serviceSuggestionList);

      handleSuggestionSelect(
        getServiceDropdownId(newServiceData),
        serviceSuggestionList
      );
    }
  };

  return (
    <ServiceRowItemUi
      isDisabled={disabled}
      serviceName={currService.service_name}
      setServiceName={handleServiceValueChange}
      serviceSearchOptions={serviceSearchOptions}
      servicesSuggestionsList={servicesSuggestionList}
      service={service}
      handleServiceSuggestionSelect={handleSuggestionSelect}
      handleCreateNewService={handleNewCreateService}
      rowIndex={index}
      onAmountChange={onAmountChange}
      onQuantityChange={onQtyChange}
      onDiscountChange={onDiscountChange}
      amountError={amountError}
      quantityError={quantityError}
      discountError={discountError}
      onRemoveService={allowRemoveService ? onRemoveService : undefined}
    />
  );
}
